import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import {
  AlertCircle,
  ArrowRight,
  BellRing,
  ClipboardCheck,
  Users,
} from "lucide-react";

// --- 1. Import Firestore functions and the database instance ---
import {
  collection,
  doc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase/firebase";

import { CONTENT } from "../constants/content";
import Reveal from "./ui/Reveal";
import Field from "./ui/Field";
import ConsentCheckbox from "./ui/ConsentCheckbox";
import AlpsPath from "./AlpsPath";
import { setHeading, setText } from "../constants/typography";
import Emphasis from "./ui/Emphasis";
import formStyles from "../styles/ui/Form.module.scss";
import styles from "../styles/Register.module.scss";

const POINT_ICONS = [Users, ClipboardCheck];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SUBMIT_TIMEOUT_MS = 15000;

/*
  Offline, the Firestore SDK queues a write and its promise never settles, which
  would leave the button stuck on "Invio in corso…". Racing a timer restores
  control to the visitor; the queued write is keyed by email, so if it does sync
  later a retry simply overwrites the same document.
*/
const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(Object.assign(new Error("timeout"), { code: "timeout" })), ms)
    ),
  ]);

const Register = () => {
  const { eyebrow, title, lead, scarcity, form, success } = CONTENT.register;

  const [formData, setFormData] = useState({ name: "", email: "", gdprConsent: false });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(""); // State for handling errors
  const reduceMotion = useReducedMotion();

  const normalizeEmail = (email) => email.trim().toLowerCase();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear a field's error as soon as the visitor starts correcting it.
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = form.errors.name;
    if (!EMAIL_PATTERN.test(formData.email.trim())) errors.email = form.errors.email;
    if (!formData.gdprConsent) errors.gdprConsent = form.errors.consent;
    return errors;
  };

  // --- 2. Updated handleSubmit function to be async and save to Firestore ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const emailKey = normalizeEmail(formData.email);
      const subscriberRef = doc(collection(db, "subscribers"), emailKey);

      // Create the document with deterministic ID to enforce uniqueness
      await withTimeout(
        setDoc(subscriberRef, {
          name: formData.name,
          email: formData.email,
          emailKey,
          gdprConsentAccepted: formData.gdprConsent,
          marketingConsent: formData.gdprConsent, // align marketing consent with GDPR checkbox
          brevoStatus: "pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
        SUBMIT_TIMEOUT_MS
      );
      setIsSubmitted(true);
    } catch (err) {
      console.error("Error adding document: ", err);
      // Permission denied likely means the document already exists
      if (err?.code === "permission-denied") {
        setError(form.errors.duplicate);
      } else {
        setError(form.errors.generic);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="register" className={styles.register} aria-label="Iscriviti al corso">
      <span className={styles.watermark} aria-hidden="true">
        ALPS
      </span>

      <div className={styles.inner}>
        <Reveal as="header" className={styles.head}>
          <span className={styles.eyebrow}>{eyebrow}</span>
          <h2 className={styles.title} itemProp="name">
            {setHeading(title)}
          </h2>
        </Reveal>

        <div className={styles.body}>
          <div className={styles.editorial}>
            <Reveal className={styles.leadBlock}>
              <p className={styles.leadQuestion}>{lead.question}</p>
              <p className={`${styles.lead} on-dark`} itemProp="description">
                {setText(lead.part1)}
                <Emphasis>{lead.boldPart}</Emphasis>
                {lead.part2}
              </p>
            </Reveal>

            <Reveal className={styles.pathWrapper} delay={0.08}>
              <AlpsPath tone="dark" />
            </Reveal>

            <Reveal className={styles.scarcity} delay={0.12}>
              <p className={styles.scarcityStat}>
                <span className={styles.scarcityValue}>{scarcity.value}</span>
                <span className={styles.scarcityUnit}>{scarcity.unit}</span>
              </p>
              <ul className={styles.scarcityList}>
                {scarcity.points.map((point, index) => {
                  const Icon = POINT_ICONS[index] || Users;
                  return (
                    <li key={point}>
                      <Icon aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          </div>

          <Reveal className={styles.formColumn} from="left" delay={0.1}>
            <div className={styles.formPanel}>
              {isSubmitted ? (
                <motion.div
                  className={styles.success}
                  role="status"
                  initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <svg
                    className={styles.successMark}
                    viewBox="0 0 52 52"
                    aria-hidden="true"
                  >
                    <circle cx="26" cy="26" r="24" />
                    <path d="M15 26.5l8 8 15-15" />
                  </svg>
                  <p className={styles.successTitle}>{success.title}</p>
                  <p className={styles.successMessage}>{success.message}</p>
                  <ScrollLink
                    to="faq"
                    smooth={true}
                    duration={600}
                    offset={-72}
                    className={styles.successLink}
                    tabIndex={0}
                  >
                    {success.nextStepText}
                    <ArrowRight aria-hidden="true" />
                  </ScrollLink>
                </motion.div>
              ) : (
                <>
                  <div className={styles.panelHead}>
                    <h3 className={styles.panelTitle}>{setHeading(form.title)}</h3>
                    <p className={styles.panelSubtitle}>{setText(form.subtitle)}</p>
                  </div>

                  <form onSubmit={handleSubmit} className={styles.form} noValidate>
                    {/*
                      `label` and `name` drive the shell, everything else on a
                      field descriptor is a native input attribute (type,
                      inputMode, autoCapitalize, enterKeyHint…) and is spread
                      straight onto the control, so adding a mobile keyboard
                      hint is a content change rather than a component change.
                    */}
                    {form.fields.map(({ name, label, ...inputProps }) => (
                      <Field
                        key={name}
                        id={name}
                        label={label}
                        value={formData[name]}
                        onChange={handleChange}
                        aria-required="true"
                        error={fieldErrors[name]}
                        {...inputProps}
                      />
                    ))}

                    <ConsentCheckbox
                      checked={formData.gdprConsent}
                      onChange={handleChange}
                      error={fieldErrors.gdprConsent}
                    >
                      {form.gdprConsentText}
                    </ConsentCheckbox>

                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className={formStyles.spinner} aria-hidden="true" />
                          {form.buttonBusyText}
                        </>
                      ) : (
                        <>
                          {form.buttonText}
                          <BellRing className={styles.buttonIcon} aria-hidden="true" />
                        </>
                      )}
                    </button>

                    {/* --- 3. Display error message if one exists --- */}
                    {error && (
                      <p className={styles.formError} role="alert">
                        <AlertCircle aria-hidden="true" />
                        {error}
                      </p>
                    )}
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Register;
